import React, { useState, useEffect } from 'react';
import { analyticsApi } from '../../services/api';
import { 
  ChartBarIcon, 
  ArrowPathIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CpuChipIcon,
  DocumentTextIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlayIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PIPELINE_STAGES = [
  { id: 'new', name: 'New', color: '#6B7280' },
  { id: 'analyzing', name: 'Analyzing', color: '#3B82F6' },
  { id: 'qualified', name: 'Qualified', color: '#10B981' },
  { id: 'proposal', name: 'Proposal', color: '#8B5CF6' },
  { id: 'submitted', name: 'Submitted', color: '#F59E0B' },
  { id: 'awarded', name: 'Awarded', color: '#059669' },
  { id: 'lost', name: 'Lost', color: '#EF4444' },
  { id: 'archived', name: 'Archived', color: '#64748B' },
];

const AGENT_COLORS = {
  'contract-scout': '#3B82F6',
  'email-agent': '#10B981',
  'vendor-matcher': '#8B5CF6',
  'monitor-agent': '#F59E0B',
  'compliance-agent': '#EF4444',
  'analyst-agent': '#6366F1',
};

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>(null);
  const [agentPerformance, setAgentPerformance] = useState<any>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportConfig, setExportConfig] = useState({ type: 'opportunities', format: 'csv' });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentPerformance = async () => {
    try {
      const response = await analyticsApi.getAgentPerformance({
        startDate: dateRange.start,
        endDate: dateRange.end
      });
      setAgentPerformance(response.data);
    } catch (error) {
      console.error('Failed to load agent performance');
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchAgentPerformance();
  }, []);

  const handleExport = async () => {
    try {
      const response = await analyticsApi.exportReport(
        exportConfig.type,
        exportConfig.format as 'csv' | 'json',
        { startDate: dateRange.start, endDate: dateRange.end }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: exportConfig.format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportConfig.type}-report.${exportConfig.format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Chart data
  const pipelineChartData = {
    labels: PIPELINE_STAGES.map(s => s.name),
    datasets: [{
      label: 'Opportunities',
      data: PIPELINE_STAGES.map(s => 
        dashboard?.pipeline?.byStage?.find((x: any) => x.stage === s.id)?.count || 0
      ),
      backgroundColor: PIPELINE_STAGES.map(s => s.color),
      borderRadius: 4,
    }]
  };

  const matchScoreChartData = {
    labels: ['90-100%', '80-89%', '70-79%', '60-69%', '50-59%', '< 50%'],
    datasets: [{
      label: 'Opportunities',
      data: [
        dashboard?.opportunities?.byMatchScore?.['90-100'] || 0,
        dashboard?.opportunities?.byMatchScore?.['80-89'] || 0,
        dashboard?.opportunities?.byMatchScore?.['70-79'] || 0,
        dashboard?.opportunities?.byMatchScore?.['60-69'] || 0,
        dashboard?.opportunities?.byMatchScore?.['50-59'] || 0,
        dashboard?.opportunities?.byMatchScore?.['<50'] || 0,
      ],
      backgroundColor: ['#059669', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#6B7280'],
      borderRadius: 4,
    }]
  };

  const agentTimelineData = agentPerformance?.timeline ? {
    labels: agentPerformance.timeline.map((t: any) => t.date),
    datasets: Object.keys(AGENT_COLORS).map(agentType => ({
      label: agentType.replace('-', ' '),
      data: agentPerformance.timeline.map((t: any) => t[agentType] || 0),
      borderColor: AGENT_COLORS[agentType as keyof typeof AGENT_COLORS],
      backgroundColor: AGENT_COLORS[agentType as keyof typeof AGENT_COLORS] + '20',
      fill: false,
      tension: 0.4,
    }))
  } : null;

  const agentStatusData = dashboard?.agents ? {
    labels: ['Completed', 'Failed', 'Running', 'Pending'],
    datasets: [{
      data: [
        dashboard.agents.completedTasks || 0,
        dashboard.agents.failedTasks || 0,
        dashboard.agents.runningTasks || 0,
        dashboard.agents.pendingTasks || 0,
      ],
      backgroundColor: ['#10B981', '#EF4444', '#3B82F6', '#F59E0B'],
      borderWidth: 0,
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-sm text-gray-500">Insights into your opportunities, agents, and pipeline performance</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { fetchDashboard(); fetchAgentPerformance(); }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Refresh
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Export
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Date Range:</span>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            onClick={fetchAgentPerformance}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Apply
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Opportunities</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.opportunities?.total?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">
            +{dashboard?.opportunities?.newThisWeek || 0} this week
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FunnelIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Pipeline</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.pipeline?.total || 0}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {dashboard?.pipeline?.conversionRate || 0}% conversion rate
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CpuChipIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Agent Tasks</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.agents?.totalTasks || 0}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {dashboard?.agents?.successRate || 0}% success rate
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Match Score</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.opportunities?.averageMatchScore || 0}%
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {dashboard?.opportunities?.highMatchCount || 0} high matches
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Distribution</h3>
          <div className="h-64">
            <Bar 
              data={pipelineChartData} 
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: { display: false }
                },
                scales: {
                  y: { beginAtZero: true, ticks: { precision: 0 } }
                }
              }} 
            />
          </div>
        </div>

        {/* Match Score Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Match Score Distribution</h3>
          <div className="h-64">
            <Bar 
              data={matchScoreChartData} 
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: { display: false }
                },
                scales: {
                  y: { beginAtZero: true, ticks: { precision: 0 } }
                }
              }} 
            />
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Task Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Task Status</h3>
          <div className="h-64 flex items-center justify-center">
            {agentStatusData && (
              <Doughnut 
                data={agentStatusData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right' }
                  },
                  cutout: '60%',
                }} 
              />
            )}
          </div>
        </div>

        {/* Agent Performance Timeline */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Activity Timeline</h3>
          <div className="h-64">
            {agentTimelineData ? (
              <Line 
                data={agentTimelineData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: false }
                  },
                  scales: {
                    y: { beginAtZero: true, ticks: { precision: 0 } }
                  }
                }} 
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No timeline data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Agent Performance Details</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Tasks</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agentPerformance?.byAgent ? (
                Object.entries(agentPerformance.byAgent).map(([agentType, stats]: [string, any]) => (
                  <tr key={agentType}>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900 capitalize">
                        {agentType.replace('-', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{stats.total}</td>
                    <td className="px-6 py-4 text-green-600">{stats.completed}</td>
                    <td className="px-6 py-4 text-red-600">{stats.failed}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-24">
                          <div 
                            className="bg-brand-600 h-2 rounded-full"
                            style={{ width: `${stats.successRate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{stats.successRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {stats.averageDuration ? `${Math.round(stats.averageDuration / 60)}m` : 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No performance data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trends Section */}
      {dashboard?.trends && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Trends</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">New Opportunities</p>
              <p className="text-2xl font-bold text-gray-900">{dashboard.trends.newOpportunities || 0}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Stage Changes</p>
              <p className="text-2xl font-bold text-gray-900">{dashboard.trends.stageChanges || 0}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Agent Tasks Completed</p>
              <p className="text-2xl font-bold text-gray-900">{dashboard.trends.agentTasksCompleted || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Export Report</h2>
              <button onClick={() => setShowExportModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                <select
                  value={exportConfig.type}
                  onChange={(e) => setExportConfig({ ...exportConfig, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="opportunities">Opportunities</option>
                  <option value="agents">Agent Performance</option>
                  <option value="pipeline">Pipeline Activity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                <select
                  value={exportConfig.format}
                  onChange={(e) => setExportConfig({ ...exportConfig, format: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
